import { StandardEvents } from '@shopify/events';
import { quoteSubset } from './quote-mode-core.js';
import { propertyQuoteMatches } from './quote-property-condition.js';

export const strings = JSON.parse(document.getElementById('QuoteModeStrings')?.textContent || '{}');
export const routeRoot = window.Shopify?.routes?.root || '/';
export const pendingCartTasks = window.__quotePendingCartTasks ||= new Set();

export async function waitForCart() {
  const deadline = Date.now() + 15000;
  do {
    await Promise.allSettled([...pendingCartTasks]);
    // Fee additions can be scheduled by the promise resolution of a parent addition.
    await new Promise((resolve) => setTimeout(resolve, 50));
    if (Date.now() > deadline) throw new Error('pending');
  } while (pendingCartTasks.size || window.customOptionFeesManager?.processing);
}

export async function fetchCart() {
  const response = await fetch(`${routeRoot}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!response.ok) throw new Error('Unable to load cart');
  return response.json();
}

export async function fetchQuoteState() {
  for (let attempt = 0; attempt < 2; attempt++) {
    const cart = await fetchCart();
    const url = new URL(routeRoot, location.origin);
    url.searchParams.set('section_id', 'quote-cart-state');
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error('Unable to classify cart');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const state = JSON.parse(doc.querySelector('[data-quote-cart-state]')?.textContent || 'null');
    const checkout = JSON.parse(doc.querySelector('[data-quote-checkout-state]')?.textContent || 'null');
    if (!state || !checkout) throw new Error('Unable to classify cart');
    try {
      return { cart, quote: quoteSubset(cart, state), state, checkout };
    } catch (error) {
      if (attempt) throw error;
    }
  }
}

export function formPolicy(form) {
  return JSON.parse(form?.querySelector('[data-quote-policy]')?.textContent || 'null');
}

export function setQuoteIntent(form, explicit) {
  const policy = formPolicy(form);
  if (!policy) return;
  const conditional = propertyQuoteMatches(form);
  const quote = conditional || (Boolean(policy.scope) && (policy.behavior === 'quote_only' || (policy.behavior === 'optional' && explicit)));
  form.querySelectorAll('[data-quote-property]').forEach((input) => {
    input.disabled = !quote;
    if (input.name === 'properties[_quote_scope]') input.value = policy.scope || 'property';
  });
}

let lastCart;
function updateForm(form) {
  const policy = formPolicy(form);
  if (!policy) return;
  const quantityInput = form.querySelector('[name="quantity"]');
  const quantity = form.querySelector('variant-quantity-grid')?.active
    ? form.querySelector('variant-quantity-grid').totalQuantity
    : Number(quantityInput?.value || 1);
  const total = (lastCart?.items || []).filter((item) => String(item.product_id) === String(policy.productId) &&
    !item.properties?._fee_parent_key && !item.properties?._fee_parent_group && !item.properties?._fee_parent_product_id)
    .reduce((n, item) => n + item.quantity, 0);
  const quote = propertyQuoteMatches(form) || (Boolean(policy.scope) && (policy.behavior === 'quote_only' || (policy.behavior === 'threshold' && policy.threshold > 0 && total + quantity >= policy.threshold)));
  setQuoteIntent(form, false);
  const button = form.querySelector('button[type="submit"]:not([data-add-to-quote]) [data-quote-add-label]');
  // Unavailable/select-option/access states keep their existing labels.
  if (button && !button.closest('button').disabled) {
    const label = quote ? policy.label : policy.normalLabel;
    if (button.textContent !== label) button.textContent = label;
  }
  form.querySelector('[data-add-to-quote]')?.toggleAttribute('disabled', Boolean(form.querySelector('[name="id"]')?.disabled ||
    form.querySelector('button[type="submit"]:not([data-add-to-quote])')?.disabled));
  const section = form.closest('.shopify-section');
  const stickyText = section?.querySelector('.sticky-add-to-cart__button:not(:disabled) .add-to-cart-text__content > span:first-child');
  if (stickyText) stickyText.textContent = quote ? policy.label : policy.normalLabel;
  const stickyQuote = section?.querySelector('[data-sticky-add-to-quote]');
  if (stickyQuote) stickyQuote.disabled = Boolean(form.querySelector('[data-add-to-quote]')?.disabled);
  // Prices are rendered by Liquid; refresh their section when the cart crosses a threshold.
  (section || form).querySelectorAll(`[data-quote-price-product="${policy.productId}"] [data-quote-price-normal]`).forEach((normal) => {
    const wrapper = normal.parentElement;
    const required = wrapper.querySelector('[data-quote-price-required]');
    const prefix = wrapper.querySelector('[data-quote-price-prefix]');
    const quotePrice = quote;
    normal.hidden = quotePrice && policy.priceDisplay === 'hidden';
    if (required) required.hidden = !normal.hidden;
    if (prefix) { prefix.hidden = !quotePrice || policy.priceDisplay === 'hidden'; prefix.textContent = policy.prefix || ''; }
  });
}

let refreshTimer;
async function refreshForms() {
  if (!document.querySelector('[data-quote-policy]')) return;
  document.querySelectorAll('form:has([data-quote-policy])').forEach(updateForm);
  try {
    lastCart = await fetchCart();
    document.querySelectorAll('form:has([data-quote-policy])').forEach(updateForm);
  } catch { /* Submission/cart rendering remains authoritative when preview fetch fails. */ }
}
function scheduleRefresh() {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(refreshForms, 100);
}
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (!event.promise) return;
  const task = event.promise;
  pendingCartTasks.add(task);
  task.finally(() => { pendingCartTasks.delete(task); scheduleRefresh(); }).catch(() => {});
});
document.addEventListener('cart:update', scheduleRefresh);
document.addEventListener('quantity-selector:update', scheduleRefresh);
document.addEventListener('property-quote:change', () => {
  document.querySelectorAll('form:has([data-quote-policy])').forEach(updateForm);
});
document.addEventListener('change', (event) => {
  if (event.target?.name === 'quantity' && event.target.form) updateForm(event.target.form);
});
document.addEventListener('shopify:section:load', scheduleRefresh);
document.addEventListener(StandardEvents.productSelect, scheduleRefresh);
document.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-sticky-add-to-quote]');
  if (!button) return;
  const target = button.closest('.shopify-section')?.querySelector('form [data-add-to-quote]');
  if (target && !target.disabled) target.click();
});
new MutationObserver((mutations) => {
  if (mutations.some((mutation) => [...mutation.addedNodes].some((node) => node instanceof Element &&
    (node.matches('[data-quote-policy]') || node.querySelector('[data-quote-policy]'))))) scheduleRefresh();
}).observe(document.body, { childList: true, subtree: true });
window.addEventListener('pageshow', scheduleRefresh);
scheduleRefresh();
